import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient, PublicScorePreview } from "@/lib/api";
import { useAuthStore } from "@/stores/authStore";
import { Button } from "@/components/ui/button";
import { Users, Target, Film, Sparkles, ExternalLink, Lock } from "lucide-react";
import { ScoreHero } from "@/components/score/ScoreHero";
import { TrafficLightCard, TrafficLightStatus } from "@/components/score/TrafficLightCard";
import logo from '../assets/images/logo-artie.png';
import { format } from "date-fns";

const getTrafficLightColor = (status: string) => {
  const s = status.toLowerCase();
  if (s === "green" || s === "high") return "green";
  if (s === "yellow" || s === "medium") return "yellow";
  if (s === "red" || s === "low") return "red";
  return "gray";
};

export const SharedScorePage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scoreData, setScoreData] = useState<PublicScorePreview | null>(null);

  useEffect(() => {
    const fetchSharedScore = async () => {
      if (!token) {
        setError("Invalid share link");
        setLoading(false);
        return;
      }

      try {
        const data = await apiClient.getSharedScore(token);
        setScoreData(data);
      } catch (err: any) {
        setError(err.message || "Failed to load shared score");
      } finally {
        setLoading(false);
      }
    };

    fetchSharedScore();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--background)]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !scoreData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--background)] p-4">
        <div className="max-w-md w-full p-8 bg-[var(--card)] border border-[var(--border)] rounded-2xl text-center">
          <div className="p-4 bg-red-50 dark:bg-red-950/20 rounded-full mb-6 inline-block">
            <Lock className="w-12 h-12 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold font-cabinet mb-2">Link Not Found</h1>
          <p className="text-muted-foreground mb-6">{error || "This share link is invalid or has expired."}</p>
          <Button onClick={() => navigate("/")} className="w-full">
            Go to Homepage
          </Button>
        </div>
      </div>
    );
  }

  const scoreValue = scoreData.artie_score;
  const reachColor = getTrafficLightColor(scoreData.reach_status);
  const brandFitColor = getTrafficLightColor(scoreData.brand_fit_status);
  const prodReadinessColor = getTrafficLightColor(scoreData.production_readiness_status);

  // If user is authenticated and has full score data, show full dashboard
  if (scoreData.is_authenticated && scoreData.full_score_data) {
    const fullScore = scoreData.full_score_data;
    
    return (
      <div className="min-h-screen bg-[var(--background)]">
        {/* Header */}
        <div className="border-b bg-[var(--card)] backdrop-blur">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src={logo} alt="Artie" className="h-10 w-10 rounded-full" />
              <span className="font-bold text-xl font-cabinet">Artie</span>
            </div>
            <Button onClick={() => navigate("/dashboard")}>
              Go to Dashboard
            </Button>
          </div>
        </div>

        {/* Full Score Content */}
        <div className="container mx-auto px-4 py-8 max-w-6xl">
          <div className="mb-6">
            <h1 className="text-3xl font-bold font-cabinet mb-2">{scoreData.project_title}</h1>
            <p className="text-muted-foreground">Shared by {scoreData.creator_name || "Creator"}</p>
          </div>

          <div className="space-y-8">
            <ScoreHero
              score={fullScore.artie_score}
              label={fullScore.score_label}
              viability={fullScore.budget_viability}
              targetAmount={fullScore.production_readiness_analysis?.budget_amount}
            />

            <div className="grid gap-6 md:grid-cols-3">
              <TrafficLightCard
                title="Reach"
                status={fullScore.reach_status as TrafficLightStatus}
                summary={fullScore.reach_analysis?.gap_analysis || "Analysis complete"}
                onClick={() => {}}
              />
              <TrafficLightCard
                title="Brand Fit"
                status={fullScore.brand_fit_status as TrafficLightStatus}
                summary={fullScore.brand_fit_analysis?.status === 'green' ? "Strong fit for brands" : "Optimization needed"}
                onClick={() => {}}
              />
              <TrafficLightCard
                title="Production Readiness"
                status={fullScore.production_readiness_status as TrafficLightStatus}
                summary={fullScore.production_readiness_analysis?.gaps?.length > 0
                  ? `${fullScore.production_readiness_analysis.gaps.length} gaps identified`
                  : "Ready for production"}
                onClick={() => {}}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Otherwise show limited public preview
  return (
    <div className="min-h-screen bg-[var(--background)]">
      {/* Header */}
      <div className="border-b bg-[var(--card)] backdrop-blur">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Artie" className="h-10 w-10 rounded-full" />
            <span className="font-bold text-xl font-cabinet">Artie</span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/login")} className="hidden sm:flex">
              Log In
            </Button>
            <Button onClick={() => navigate(`/signup?ref=${token}`)}>
              <span className="hidden sm:inline">Sign Up Free</span>
              <span className="sm:hidden">Sign Up</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Creator Info */}
        {scoreData.creator_name && (
          <div className="flex items-center gap-3 mb-6 animate-fade-in">
            {scoreData.creator_avatar ? (
              <img
                src={scoreData.creator_avatar}
                alt={scoreData.creator_name}
                className="w-12 h-12 rounded-full border-2 border-border"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center border-2 border-border">
                <span className="text-lg font-bold">{scoreData.creator_name[0]}</span>
              </div>
            )}
            <div>
              <p className="text-xs text-muted-foreground font-ibm">Shared by</p>
              <p className="font-semibold font-cabinet">{scoreData.creator_name}</p>
            </div>
          </div>
        )}

        {/* Hero Grid: Artie Card + Score */}
        <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {/* Artie Insight Card */}
          <div className="md:col-span-2 p-6 md:p-8 bg-[var(--card)] border border-[var(--border)] rounded-2xl flex flex-col justify-between animate-slide-up relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[var(--secondary)] to-transparent opacity-50 rounded-bl-[100px] -z-0 pointer-events-none"></div>
            
            <div className="flex items-start gap-6 relative z-10">
              <div className="relative shrink-0 hidden sm:block">
                <div className="w-20 h-20 rounded-full bg-white border-4 border-white shadow-lg overflow-hidden">
                  <img src={logo} alt="Artie" className="w-full h-full object-cover" />
                </div>
              </div>

              <div className="flex flex-col items-start gap-3 flex-1 min-w-0">
                <div className="flex flex-col">
                  <h1 className="text-xl md:text-2xl font-bold font-cabinet text-[var(--foreground)] tracking-tight">
                    {scoreData.project_title}
                  </h1>
                  <span className="text-xs text-[var(--muted-foreground)] font-ibm">
                    Artie Score Analysis
                  </span>
                </div>

                <div className="relative bg-[var(--secondary)]/30 p-4 rounded-xl rounded-tl-sm border border-[var(--border)] w-full">
                  <p className="text-[var(--foreground)] font-ibm text-sm leading-relaxed">
                    This project has been analyzed by Artie AI. Sign up to see the full breakdown, detailed recommendations, and generate your own Artie Scores.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Score Card */}
          <div className="md:col-span-1 p-6 bg-[var(--foreground)] text-[var(--background)] rounded-2xl flex flex-col items-center justify-center text-center relative overflow-hidden shadow-xl animate-slide-up delay-100">
            <div className="absolute inset-0 opacity-10 bg-white/5"></div>

            <div className="relative z-10 w-full flex flex-col items-center h-full justify-between">
              <div className="flex justify-between w-full items-center mb-4">
                <span className="text-xs font-medium opacity-70 font-ibm uppercase tracking-wider">Artie Score</span>
              </div>

              <div className="flex flex-col items-center gap-4 my-auto">
                <h2 className={`text-5xl font-bold font-cabinet tracking-tight ${
                  scoreValue >= 80 ? 'text-green-400' :
                  scoreValue >= 60 ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {scoreValue}
                </h2>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-full border border-white/10">
                  <div className={`w-2 h-2 rounded-full ${
                    scoreValue >= 80 ? 'bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.5)]' :
                    scoreValue >= 60 ? 'bg-yellow-400 shadow-[0_0_8px_rgba(250,204,21,0.5)]' : 'bg-red-400 shadow-[0_0_8px_rgba(248,113,113,0.5)]'
                  }`}></div>
                  <span className="text-sm font-medium font-ibm">out of 100</span>
                </div>
              </div>

              <div className="mt-4 text-center">
                <p className="text-xs opacity-70 font-ibm">
                  Budget Viability: {scoreData.budget_viability}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Traffic Light Cards */}
        <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 animate-slide-up delay-200">
          {/* Reach Card */}
          <div className="relative h-full group">
            <div className="absolute -top-6 -right-3 z-20">
              <div className="relative w-14 h-14 group-hover:scale-110 transition-transform duration-300">
                <img src={logo} alt="Artie" className="w-full h-full rounded-full object-cover border-4 border-background shadow-xl" />
                <div className="absolute right-10 -top-2 rotate-2 bg-primary text-primary-foreground text-[10px] font-bold px-3 py-1.5 rounded-xl shadow-lg z-20 border-2 border-background whitespace-nowrap">
                  Audience Potential
                </div>
              </div>
            </div>
            <div className="relative p-5 pt-6 bg-[var(--card)] border border-[var(--border)] rounded-2xl flex flex-col gap-3 overflow-hidden h-full">
              <div className="flex justify-between items-start z-10 w-full">
                <div className="p-2 rounded-lg bg-[var(--background)] border border-[var(--border)] text-[var(--foreground)] shadow-sm">
                  <Users className="w-4.5 h-4.5" />
                </div>
                <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${
                  reachColor === 'green' ? 'bg-green-500/10 border-green-500/20' :
                  reachColor === 'yellow' ? 'bg-yellow-500/10 border-yellow-500/20' :
                  reachColor === 'red' ? 'bg-red-500/10 border-red-500/20' :
                  'bg-gray-500/10 border-gray-500/20'
                }`}>
                  <div className={`w-1.5 h-1.5 rounded-full ${
                    reachColor === 'green' ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' :
                    reachColor === 'yellow' ? 'bg-yellow-500 shadow-[0_0_8px_rgba(234,179,8,0.6)]' :
                    reachColor === 'red' ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]' :
                    'bg-gray-500'
                  }`}></div>
                  <span className={`text-[10px] font-bold uppercase tracking-wide ${
                    reachColor === 'green' ? 'text-green-700 dark:text-green-400' :
                    reachColor === 'yellow' ? 'text-yellow-700 dark:text-yellow-400' :
                    reachColor === 'red' ? 'text-red-700 dark:text-red-400' :
                    'text-gray-700 dark:text-gray-400'
                  }`}>
                    {scoreData.reach_status}
                  </span>
                </div>
              </div>
              <div className="z-10 mt-1 flex-1">
                <h3 className="font-semibold font-cabinet text-[var(--foreground)] text-base">Reach</h3>
                <p className="text-xs text-[var(--muted-foreground)] mt-1 font-ibm leading-relaxed">
                  Audience reach potential analysis
                </p>
              </div>
            </div>
          </div>

          {/* Brand Fit Card */}
          <div className="relative h-full group">
            <div className="absolute -top-6 -right-3 z-20">
              <div className="relative w-14 h-14 group-hover:scale-110 transition-transform duration-300">
                <img src={logo} alt="Artie" className="w-full h-full rounded-full object-cover border-4 border-background shadow-xl" />
                <div className="absolute right-10 -top-2 rotate-2 bg-primary text-primary-foreground text-[10px] font-bold px-3 py-1.5 rounded-xl shadow-lg z-20 border-2 border-background whitespace-nowrap">
                  Sponsor Alignment
                </div>
              </div>
            </div>
            <div className="relative p-5 pt-6 bg-[var(--card)] border border-[var(--border)] rounded-2xl flex flex-col gap-3 overflow-hidden h-full">
              <div className="flex justify-between items-start z-10 w-full">
                <div className="p-2 rounded-lg bg-[var(--background)] border border-[var(--border)] text-[var(--foreground)] shadow-sm">
                  <Target className="w-4.5 h-4.5" />
                </div>
                <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${
                  brandFitColor === 'green' ? 'bg-green-500/10 border-green-500/20' :
                  brandFitColor === 'yellow' ? 'bg-yellow-500/10 border-yellow-500/20' :
                  brandFitColor === 'red' ? 'bg-red-500/10 border-red-500/20' :
                  'bg-gray-500/10 border-gray-500/20'
                }`}>
                  <div className={`w-1.5 h-1.5 rounded-full ${
                    brandFitColor === 'green' ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' :
                    brandFitColor === 'yellow' ? 'bg-yellow-500 shadow-[0_0_8px_rgba(234,179,8,0.6)]' :
                    brandFitColor === 'red' ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]' :
                    'bg-gray-500'
                  }`}></div>
                  <span className={`text-[10px] font-bold uppercase tracking-wide ${
                    brandFitColor === 'green' ? 'text-green-700 dark:text-green-400' :
                    brandFitColor === 'yellow' ? 'text-yellow-700 dark:text-yellow-400' :
                    brandFitColor === 'red' ? 'text-red-700 dark:text-red-400' :
                    'text-gray-700 dark:text-gray-400'
                  }`}>
                    {scoreData.brand_fit_status}
                  </span>
                </div>
              </div>
              <div className="z-10 mt-1 flex-1">
                <h3 className="font-semibold font-cabinet text-[var(--foreground)] text-base">Brand Fit</h3>
                <p className="text-xs text-[var(--muted-foreground)] mt-1 font-ibm leading-relaxed">
                  Brand partnership viability
                </p>
              </div>
            </div>
          </div>

          {/* Production Card */}
          <div className="relative h-full group">
            <div className="absolute -top-6 -right-3 z-20">
              <div className="relative w-14 h-14 group-hover:scale-110 transition-transform duration-300">
                <img src={logo} alt="Artie" className="w-full h-full rounded-full object-cover border-4 border-background shadow-xl" />
                <div className="absolute right-10 -top-2 rotate-2 bg-primary text-primary-foreground text-[10px] font-bold px-3 py-1.5 rounded-xl shadow-lg z-20 border-2 border-background whitespace-nowrap">
                  Script Feasibility
                </div>
              </div>
            </div>
            <div className="relative p-5 pt-6 bg-[var(--card)] border border-[var(--border)] rounded-2xl flex flex-col gap-3 overflow-hidden h-full">
              <div className="flex justify-between items-start z-10 w-full">
                <div className="p-2 rounded-lg bg-[var(--background)] border border-[var(--border)] text-[var(--foreground)] shadow-sm">
                  <Film className="w-4.5 h-4.5" />
                </div>
                <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${
                  prodReadinessColor === 'green' ? 'bg-green-500/10 border-green-500/20' :
                  prodReadinessColor === 'yellow' ? 'bg-yellow-500/10 border-yellow-500/20' :
                  prodReadinessColor === 'red' ? 'bg-red-500/10 border-red-500/20' :
                  'bg-gray-500/10 border-gray-500/20'
                }`}>
                  <div className={`w-1.5 h-1.5 rounded-full ${
                    prodReadinessColor === 'green' ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' :
                    prodReadinessColor === 'yellow' ? 'bg-yellow-500 shadow-[0_0_8px_rgba(234,179,8,0.6)]' :
                    prodReadinessColor === 'red' ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]' :
                    'bg-gray-500'
                  }`}></div>
                  <span className={`text-[10px] font-bold uppercase tracking-wide ${
                    prodReadinessColor === 'green' ? 'text-green-700 dark:text-green-400' :
                    prodReadinessColor === 'yellow' ? 'text-yellow-700 dark:text-yellow-400' :
                    prodReadinessColor === 'red' ? 'text-red-700 dark:text-red-400' :
                    'text-gray-700 dark:text-gray-400'
                  }`}>
                    {scoreData.production_readiness_status}
                  </span>
                </div>
              </div>
              <div className="z-10 mt-1 flex-1">
                <h3 className="font-semibold font-cabinet text-[var(--foreground)] text-base">Readiness</h3>
                <p className="text-xs text-[var(--muted-foreground)] mt-1 font-ibm leading-relaxed">
                  Production readiness assessment
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Card */}
        <div className="p-8 bg-gradient-to-br from-primary to-primary/80 text-primary-foreground rounded-2xl shadow-2xl animate-slide-up delay-300">
          <div className="flex items-start gap-6 mb-6">
            <div className="p-4 bg-white/10 rounded-2xl backdrop-blur">
              <Lock className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl md:text-3xl font-bold font-cabinet mb-2">Unlock Full Analysis</h2>
              <p className="text-primary-foreground/90 font-ibm">
                Sign up for free to view detailed breakdowns, AI recommendations, pitch deck generation, and create your own Artie Scores.
              </p>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              size="lg"
              variant="secondary"
              className="flex-1 font-semibold"
              onClick={() => navigate(`/signup?ref=${token}`)}
            >
              <Sparkles className="mr-2 h-5 w-5" />
              Create Free Account
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="flex-1 bg-transparent border-primary-foreground/20 hover:bg-primary-foreground/10 text-primary-foreground font-semibold"
              onClick={() => navigate("/login")}
            >
              I Have an Account
              <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-muted-foreground font-ibm">
            Analyzed {scoreData.created_at ? format(new Date(scoreData.created_at), "MMM d, yyyy") : "recently"} • Powered by Artie AI
          </p>
        </div>
      </div>
    </div>
  );
};